import { ExpandableListBlock } from '@care/react-component-lib';

import { Box, Grid, Link, Typography } from '@mui/material';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import { SEOSegmentationSearchingFor, SEOSegmentationPageType } from '@/__generated__/globalTypes';
import {
  getSEOSegmentationLinkSections,
  getSEOSegmentationLinkSections_getSEOSegmentationLinkSections as RightRail,
  getSEOSegmentationLinkSections_getSEOSegmentationLinkSections_linkList as LinkList,
} from '@/__generated__/getSEOSegmentationLinkSections';
import { SxClassProps } from '@/types/common';
import { usePageAttributes } from './contexts/PageAttributesContext';

const classes: SxClassProps = {
  rightRailsContainer: {
    marginBottom: 3,
  },
  linkHeaders: {
    marginBottom: 1.5,
  },
  linkCategories: {
    color: 'care.grey.700',
    cursor: 'pointer',
    lineHeight: '30px',
    display: 'block !important',
  },
  pageStat: {
    color: 'care.grey.700',
    lineHeight: '30px',
    listStyle: 'none',
  },
};

interface RightRailProps {
  rightRails: getSEOSegmentationLinkSections['getSEOSegmentationLinkSections'];
}

const RightRails = ({ rightRails }: RightRailProps) => {
  const {
    avgPayRate,
    city,
    state,
    segmentNamePlural,
    totalNumberOfResults,
    avgReviewRating,
    searchingFor,
    pageType,
  } = usePageAttributes();

  const showRightRailsStatsTable =
    pageType === SEOSegmentationPageType.LOCAL &&
    searchingFor !== SEOSegmentationSearchingFor.ALL_JOB &&
    searchingFor !== SEOSegmentationSearchingFor.SEEKER_JOB &&
    searchingFor !== SEOSegmentationSearchingFor.SMB_JOB &&
    searchingFor !== SEOSegmentationSearchingFor.SMB_PROVIDER;

  const filterRightRails = () => {
    let rightRailSections: getSEOSegmentationLinkSections['getSEOSegmentationLinkSections'] =
      rightRails;

    if (!rightRailSections) {
      return [];
    }
    if (rightRailSections[0].title === 'Looking for help?') {
      rightRailSections = rightRailSections.slice(1);
    }

    return rightRailSections.filter((rightRail: RightRail) => !rightRail.title.includes('Article'));
  };

  const rightRailSections = filterRightRails();

  return (
    <Grid item xs={12}>
      {showRightRailsStatsTable && (
        <Box key={`About ${city}, ${state} ${segmentNamePlural}`} sx={classes.rightRailsContainer}>
          <Typography variant="h4" color="primary" sx={classes.linkHeaders}>
            {`About ${city}, ${state} ${segmentNamePlural}`}
          </Typography>
          <Box component="li" sx={classes.pageStat}>
            Total Providers: {totalNumberOfResults}
          </Box>
          <Box component="li" sx={classes.pageStat}>
            Average Rating: {avgReviewRating}/5 stars
          </Box>
          <Box component="li" sx={classes.pageStat}>
            Average Rate: {avgPayRate}/hr
          </Box>
        </Box>
      )}
      {rightRailSections.map((rightRail: RightRail) => (
        <Box key={rightRail.title} sx={classes.rightRailsContainer}>
          <Typography variant="h4" color="primary" sx={classes.linkHeaders}>
            {rightRail.title}
          </Typography>

          {rightRail.linkList.length > 20 ? (
            <ExpandableListBlock minItems={20}>
              {rightRail.linkList.map((link: LinkList) => (
                <Link
                  variant="link2"
                  sx={classes.linkCategories}
                  href={link.url}
                  key={link.content}>
                  <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(he.decode(link.content)),
                    }}
                  />
                </Link>
              ))}
            </ExpandableListBlock>
          ) : (
            <>
              {rightRail.linkList.map((link: LinkList) => (
                <Link
                  variant="link2"
                  sx={classes.linkCategories}
                  href={link.url}
                  key={link.content}>
                  <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(he.decode(link.content)),
                    }}
                  />
                </Link>
              ))}
            </>
          )}
        </Box>
      ))}
    </Grid>
  );
};

export default RightRails;
